import config from "./config";

/**
 * Upload an image to the FlexiFunnels image microservice and return its public
 * URL.
 *
 * Mirrors the legacy learner app's profile upload (Course_Membership
 * src/pages/profile.js) field for field — same endpoint, same FormData keys,
 * same `cloud_type` / `upload_type` constants — so images this app stores land
 * in the same place, in the same shape, as every other FlexiFunnels surface.
 *
 * Deliberately uses `fetch`, NOT axios. axiosSetup installs a GLOBAL response
 * interceptor that treats any 401 as "the learner's session expired": it wipes
 * the stored member and hard-redirects to the login page. This request carries
 * the member's JWT to a THIRD-PARTY host, so a token that service won't accept
 * would sign the member out of an app they are still perfectly logged in to.
 * fetch bypasses the interceptor, keeping a CDN auth failure a local error.
 */

/** Only what the service is known to take — the legacy uploader's own guard. */
export const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png"];

/**
 * The image's pixel dimensions, which the service wants alongside the file.
 *
 * Read from an off-DOM <img> the same way the legacy uploader did. Falls back to
 * 0×0 rather than rejecting: dimensions are metadata, and a browser that won't
 * decode the preview shouldn't block the upload itself.
 */
function readDimensions(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const size = { width: img.naturalWidth || img.width || 0, height: img.naturalHeight || img.height || 0 };
      URL.revokeObjectURL(url);
      resolve(size);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ width: 0, height: 0 });
    };
    img.src = url;
  });
}

/**
 * @param file    the File from an <input type="file">
 * @param userId  membership_user id (the legacy `userId` field)
 * @param jwt     the member's access token, sent as `Authorization: Bearer`
 * @returns the uploaded image's public URL
 * @throws  Error with a message suitable for a toast
 */
export async function uploadImage(file, { userId, jwt }) {
  if (!file) throw new Error("No file selected.");

  const { width, height } = await readDimensions(file);

  const fd = new FormData();
  // Field order and names copied from the legacy uploader — the service reads
  // them positionally in some paths, so this is not merely cosmetic.
  fd.append("file", file, file.name || "");
  fd.append("userId", String(userId ?? ""));
  fd.append("width", String(width));
  fd.append("height", String(height));
  fd.append("type", file.type);
  fd.append("name", file.name || "");
  fd.append("cloud_type", "2");
  fd.append("upload_type", "2");

  let res;
  try {
    res = await fetch(`${config.IMAGE_CDN_URL}/upload-image`, {
      method: "POST",
      // No Content-Type: the browser must set the multipart boundary itself.
      headers: jwt ? { authorization: `Bearer ${jwt}` } : undefined,
      body: fd,
      redirect: "follow",
    });
  } catch {
    // Network-level failure (offline, DNS, CORS preflight refused).
    throw new Error("Couldn't reach the image server.");
  }

  if (!res.ok) {
    throw new Error(
      res.status === 401 || res.status === 403
        ? "The image server rejected this upload."
        : "Image upload failed. Please try again."
    );
  }

  let body;
  try {
    body = await res.json();
  } catch {
    throw new Error("Image upload failed. Please try again.");
  }

  const url = body?.results?.imgUrl;
  if (!url) {
    // A 200 with no URL is still a failure; surfacing the service's own message
    // beats a generic one when it bothered to send one.
    throw new Error(body?.message || "Image upload failed. Please try again.");
  }
  return url;
}
